import { Dialog } from '../../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../../widgets/formControl';
import { Icon } from '../../../../../../../widgets/icon';
import { Input } from '../../../../../../../widgets/input';
import { Link } from '../../../../../../../widgets/buttons';
import { Select } from '../../../../../../../widgets/select';
import { TextArea } from '../../../../../../../widgets/textArea';
import React from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { Separator } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import WarningIcon from 'src/shared/icons/exclamation-triangle-solid.svg';
import styled from 'styled-components';
import {
    ComposerAttributeOptionNode,
    ComposerEcommerceAttribute
} from '../../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { composerQueriesMerchantProductOptionsQuery } from '../../__generated__/composerQueriesMerchantProductOptionsQuery.graphql';
import ComposerUploadComponent from '../../components/composerUploadComponent';
import { composerMerchantOptionsQuery } from '../../composerQueries';
import { tryParsePlatformValue } from '../../helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../helpers/composerNodesAdapter';

const ComposerSaveOptionDialogWindow = styled(Dialog)`
    max-width: 600px;
`;

const ComposerSaveOptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-height: 600px;
    overflow-y: auto;
    align-items: center;
    padding: 2em;
    justify-content: start;
`;

interface ComposerSaveOptionProps {
    onConfirm: (option: ComposerAttributeOptionNode | null) => void;
    productCode: string;
    salesChannelID: string;
    passedOption?: ComposerAttributeOptionNode | null;
    attributeID: string;
    selectedPlatformAttribute?: ComposerEcommerceAttribute | null;
    edit?: boolean;
}

const WarningIconContainer = styled(Icon)`
    margin-left: 10px;
    cursor: default !important;
`;
const ComposerSaveOptionDialog: React.FC<ComposerSaveOptionProps> = ({
    onConfirm,
    productCode = '',
    salesChannelID = '',
    edit = false,
    passedOption,
    attributeID,
    selectedPlatformAttribute = null
}) => {
    const fetchedPlatformAttributes = useLazyLoadQuery<composerQueriesMerchantProductOptionsQuery>(composerMerchantOptionsQuery, {
        salesChannelId: salesChannelID,
        sku: productCode
    });
    const [parsedOption, setParsedOption] = React.useState<MPlaza.AttributeOption>(ComposerAttributeNodeAdapter.fromOptionNode(passedOption || null));
    const passedPlatformOption = React.useMemo(() => {
        return tryParsePlatformValue(parsedOption.get('optionCode'));
    }, [parsedOption]);
    const [selectedPlatformOption, setSelectedPlatformOption] = React.useState<{ label: string; id: string; selected: boolean } | null>(
        passedPlatformOption
            ? {
                  label: passedPlatformOption.label,
                  id: passedPlatformOption.id,
                  selected: true
              }
            : null
    );

    const platformOptions = fetchedPlatformAttributes?.merchantProductOptions?.find((attr) => attr.optionID === selectedPlatformAttribute?.id)?.values;

    const platformOptionsList = [
        {
            label: '',
            id: '',
            selected: !selectedPlatformOption
        },
        ...(platformOptions?.map((value) => ({
            label: value.label,
            id: value.valueID,
            selected: selectedPlatformOption?.id === value.valueID
        })) || [])
    ];
    const handleConfirm = () => {
        if (selectedPlatformOption?.id !== '') {
            const platformOptionValue = {
                label: selectedPlatformOption?.label,
                id: selectedPlatformOption?.id,
                selected: true
            };
            parsedOption.set('optionCode', JSON.stringify(platformOptionValue));
        }
        onConfirm(ComposerAttributeNodeAdapter.toNodeOption(attributeID, parsedOption));
    };

    const title = edit ? T._('Edit Option', 'Admin') : T._('Create New Option', 'Admin');

    return (
        <Dialog windowDecorator={ComposerSaveOptionDialogWindow} title={title} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <ComposerSaveOptionContainer>
                <div>
                    {T._('Do you need help?', 'Admin')}{' '}
                    <Link
                        href='https://zakeke.zendesk.com/hc/en-us/articles/12934839221020-Attributes-Options-and-Actions#h_01G0MHCK9C0KKE90D04WE4SVM4'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('Read here', 'Admin')}
                    </Link>
                </div>
                {selectedPlatformAttribute && (
                    <>
                        <FormControl
                            label={T._('Select an option from the store', 'Admin')}
                            iconTooltip={selectedPlatformAttribute && platformOptions && platformOptions.length < 1 && <WarningIcon />}
                            tooltipLabelHtml={
                                selectedPlatformAttribute &&
                                platformOptions &&
                                platformOptions.length < 1 &&
                                T._('Store options are not available, please create them.', 'Admin')
                            }
                        >
                            <Select
                                value={selectedPlatformOption?.id || ''}
                                onChange={(e) => {
                                    const selectedValue = e;

                                    if (selectedValue === '') {
                                        setSelectedPlatformOption(null);
                                        parsedOption?.set('optionCode', '');
                                        setParsedOption((prev) => prev.clone());
                                    }
                                    const selectedOption = platformOptions?.find((option) => option.valueID === selectedValue);
                                    setSelectedPlatformOption({
                                        label: selectedOption?.label || '',
                                        id: selectedOption?.valueID || '',
                                        selected: true
                                    });
                                    setParsedOption((prev) => prev.clone());
                                }}
                            >
                                {platformOptionsList?.map((attr) => (
                                    <option key={attr?.label} value={attr?.id || ''} selected={attr?.selected}>
                                        {attr?.label}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <Separator />
                    </>
                )}
                <FormControl label={T._('Option Name', 'Admin')}>
                    <Input
                        type='text'
                        value={parsedOption.get('name') || ''}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            parsedOption.set('name', newValue);
                            setParsedOption((prev) => prev.clone());
                        }}
                        placeholder={T._('Option Name', 'Admin')}
                    />
                </FormControl>
                {(!selectedPlatformOption || selectedPlatformOption.id === '') && (
                    <FormControl label={T._('Option Code', 'Admin')}>
                        <Input
                            type='text'
                            value={parsedOption.get('optionCode') || ''}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                parsedOption.set('optionCode', newValue);
                                setParsedOption((prev) => prev.clone());
                            }}
                            placeholder={T._('Option Code', 'Admin')}
                        />
                    </FormControl>
                )}
                <FormControl label={T._('Option Description', 'Admin')}>
                    <TextArea
                        value={parsedOption.get('description') || ''}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            parsedOption.set('description', newValue);
                            setParsedOption((prev) => prev.clone());
                        }}
                        placeholder={T._('Option Description', 'Admin')}
                    />
                </FormControl>
                <FormControl label={T._('Preview Image', 'Admin')}>
                    <ComposerUploadComponent
                        passedImgUrl={parsedOption.get('imageUrl') || ''}
                        onImagePick={(imageUrl) => {
                            parsedOption.set('imageUrl', imageUrl);
                            setParsedOption((prev) => prev.clone());
                        }}
                    />
                </FormControl>
                {!selectedPlatformAttribute && (
                    <>
                        <Separator />
                        {/* <FormControl label={T._('Pricing', 'Admin')}>
						<Checkbox
							checked={parsedOption.get('isPriceEnabled') || false}
							label={T._('Adjust Price', 'Admin')}
							onChange={(e) => {
								const isChecked = e.target.checked;
								parsedOption.set('isPriceEnabled', isChecked);
								setParsedOption((prev) => prev.clone());
							}}
						/>
					</FormControl> */}
                    </>
                )}
            </ComposerSaveOptionContainer>
        </Dialog>
    );
};

export default ComposerSaveOptionDialog;
